import React, { useEffect, useState } from 'react';
import { biometricsApi } from '../api/biometricsApi';
import { appToast } from '../../../../components/AppToast';

export default function SwipeDetailModal({
  isOpen,
  onClose,
  record,
}) {
  const [swipes, setSwipes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !record?.empId || !record?.aDate) {
      setSwipes([]);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    biometricsApi
      .getSwipeDetail(record.empId, record.aDate)
      .then((data) => {
        if (isMounted) {
          setSwipes(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => {
        console.error(err);
        if (isMounted) {
          appToast.error('Failed to load swipe details.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, record]);

  if (!isOpen || !record) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="w-full max-w-xl rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-xl">history</span>
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-on-surface">
                Biometric Swipe Logs
              </h3>
              <p className="text-xs text-on-surface-variant">
                {record.name} {record.idCardNo ? `(${record.idCardNo})` : ''} • Date: {record.aDate}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition"
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="my-5">
          {isLoading ? (
            <div className="py-12 text-center flex flex-col items-center justify-center gap-2">
              <div className="h-7 w-7 animate-spin rounded-full border-3 border-primary border-t-transparent" />
              <p className="text-xs font-semibold text-on-surface-variant">Loading swipe records...</p>
            </div>
          ) : swipes.length === 0 ? (
            <div className="py-10 text-center text-on-surface-variant">
              <span className="material-symbols-outlined text-3xl text-outline-variant">info</span>
              <p className="mt-2 text-xs font-medium">No individual swipe records found for this date.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs font-semibold text-on-surface-variant px-1">
                <span>Total Swipes: <strong className="text-on-surface">{swipes.length}</strong></span>
                <span className="text-[11px] text-on-surface-variant">Ordered by timestamp</span>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-2xl border border-outline-variant/25 bg-surface-container-low/30">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-outline-variant/20 bg-surface-container-low text-[11px] font-bold uppercase tracking-wider text-on-surface-variant sticky top-0">
                    <tr>
                      <th className="px-4 py-2.5 w-12 text-center">#</th>
                      <th className="px-4 py-2.5">Time (HH:MM:SS)</th>
                      <th className="px-4 py-2.5 text-center">Device / Machine</th>
                      <th className="px-4 py-2.5 text-right">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/15">
                    {swipes.map((s, idx) => {
                      const timeStr = s.Swipes || s.swipes || '—';
                      const machineNo = s.MachinNo ?? s.machin_no ?? 0;
                      const isManual = machineNo === 0;

                      return (
                        <tr key={s.ID ?? s.id ?? idx} className="hover:bg-surface-container-low/60 transition-colors">
                          <td className="px-4 py-2.5 text-center font-mono text-on-surface-variant font-medium">
                            {idx + 1}
                          </td>
                          <td className="px-4 py-2.5 font-bold font-mono text-on-surface">
                            {timeStr}
                          </td>
                          <td className="px-4 py-2.5 text-center font-mono text-on-surface-variant">
                            {isManual ? '—' : `Device #${machineNo}`}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {isManual ? (
                              <span className="inline-flex items-center rounded-md bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                                Manual Punch
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                                Biometric
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-outline-variant/15 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
